// src/pages/document/DocumentTypeListPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocumentDocumentType, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

/**
 * Document type list page.
 */
export const DocumentTypeListPage = observer(() => {
  const store = useDocumentDocumentType();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');

  useEffect(() => {
    store.ListDocumentTypes({
      pagination: {
        cursor: searchParams.get('cursor') || '',
        limit: 20,
        direction: 'forward',
      },
      sort: [],
      date_range: {} as any,
      filters: {} as any,
    });
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  const handleDelete = async (typeId: string) => {
    if (!confirm(t('documentType.deleteConfirm'))) return;
    try {
      await store.DeleteDocumentType(typeId);
    } catch {
      // Error is handled by store
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/documents" className="text-muted-foreground hover:text-foreground">
              {t('document.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('documentType.list')}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{t('documentType.list')}</h1>
        </div>
        <Link to="/documents/types/new">
          <Button>{t('documentType.create')}</Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth={false}
              className="flex-1"
            />
            <Button type="submit" variant="secondary">
              {t('common.search')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Slug</TableHead>
              <TableHead>Display Name</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={6} message={t('documentType.noItems')} />
            ) : (
              store.items.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    <Link
                      to={`/documents/types/${item.entity?.id || item.id}`}
                      className="text-sm font-medium text-primary hover:text-primary/80"
                    >
                      {String(item.slug ?? '')}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {String(item.display_name ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.product_id ?? '')}
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? 'success' : 'secondary'}>
                      {item.is_active ? t('common.yes') : t('common.no')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {String(item.created_at ?? '')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/documents/types/${item.entity?.id || item.id}/edit`}>
                        <Button variant="ghost" size="sm">{t('common.edit')}</Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => handleDelete(item.entity?.id || item.id)}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default DocumentTypeListPage;
